<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { mapState } from "vuex";
import { PRODUCT_MODULE } from "@/state/modules/product";
import { CATALOG_MODULE } from "@/state/modules/catalog";
import { productMethods, catalogMethods } from "@/state/helpers";
import { accountingDriver, accountingFamily } from "@/utils/capability.helper";
import CKEditor from "@ckeditor/ckeditor5-vue";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

export default {
  page: { title: i18n.t("module.product") },
  components: { Layout, PageHeader, ckeditor: CKEditor.component },
  data() {
    return {
      flagLoading: true,
      flagSaving: false,
      canExchange: false,
      isShowingInSalesDocs: false,
      showingFlagSaving: false,
      showInApp: true,
      showInAppSaving: false,
      editor: ClassicEditor,
      editorConfig: {
        toolbar: ['bold', 'italic', 'link', '|', 'bulletedList', 'numberedList', '|', 'undo', 'redo'],
      },
      description: "",
      mediaUrls: [],
      mediaToRemove: new Set(),
      newFiles: [],
      catalogSaving: false,
      unitModal: false,
      unitSaving: false,
      unitForm: { uuid: null, uom: "", rate: 1, price: 0, is_default: false },
      unitErrors: {},
    };
  },
  computed: {
    ...mapState(PRODUCT_MODULE, ["details"]),
    ...mapState(CATALOG_MODULE, { catalogDetails: "details", catalogUoms: "uoms" }),
    p() { return this.details || {}; },
    isLite() { return accountingFamily() === "lite"; },
    driverLabel() {
      const driver = accountingDriver();
      if (!driver) return i18n.t("accounting.ledger");
      return this.$te(`accounting.driverName.${driver}`) ? i18n.t(`accounting.driverName.${driver}`) : driver;
    },
    // Units are written here only where the ledger has no UOM master (Xero, QuickBooks).
    // Editing needs the full rows (uuid / editable), which only /catalog/{code}/uoms carries.
    canEditUnits() { return this.$supports("uom") === false && this.$can("Update Catalog") && this.$can("Read Catalog"); },
    /*
     * /catalog/{code}/uoms carries uuid / is_default / editable, which editing
     * needs; `prices` on the product is the read-only subset the ledger already
     * returned. Preferring the first unconditionally emptied this table on a
     * backend that has no catalogue-sync module to answer with.
     */
    unitRows() {
      return this.catalogUoms && this.catalogUoms.length ? this.catalogUoms : (this.p.prices || []);
    },
    unitFields() {
      const fields = [
        { key: "uom", label: i18n.t("catalogSync.unit") },
        { key: "rate", label: i18n.t("catalogSync.rate") },
        { key: "price", label: i18n.t("label.unitPrice") },
        { key: "is_default", label: i18n.t("catalogSync.default") },
      ];
      if (this.canEditUnits) fields.push({ key: "action", label: i18n.t("label.action") });
      return fields;
    },
    title() {
      return this.p.itemCode
        ? `${this.p.itemCode} — ${this.p.description || ""}`
        : i18n.t("module.product");
    },
    items() {
      return [
        { text: i18n.t("module.inventory"), href: "/" },
        { text: i18n.t("module.product"), href: "/products" },
        { text: this.p.itemCode || i18n.t("label.view"), active: true },
      ];
    },
    visibleMedia() {
      return this.mediaUrls
        .map((url, idx) => ({ url, path: this.catalogDetails?.media?.[idx], idx }))
        .filter((m) => !this.mediaToRemove.has(m.idx));
    },
  },
  async created() {
    const code = this.$route.params.itemCode;
    await this.getProduct({ itemCode: code });
    // Only editing needs the richer rows; reading is served by `prices` above,
    // so a ledger that owns its own units never asks for them.
    if (this.canEditUnits) await this.getCatalogUoms({ itemCode: code });
    try {
      const r = await this.getCatalogByItemCode({ itemCode: code });
      const d = r?.data || {};
      this.canExchange = !!d.can_exchange;
      this.isShowingInSalesDocs = !!d.is_showing_in_sales_docs;
      this.showInApp = d.is_active === undefined ? true : !!d.is_active;
      this.description = d.description || "";
      this.mediaUrls = d.media_urls || [];
    } catch (_) { /* no catalog row yet */ }
    this.flagLoading = false;
  },
  methods: {
    ...productMethods,
    ...catalogMethods,
    openAddUnit() {
      this.unitForm = { uuid: null, uom: "", rate: 1, price: 0, is_default: !this.unitRows.length };
      this.unitErrors = {};
      this.unitModal = true;
    },
    openEditUnit(row) {
      this.unitForm = {
        uuid: row.uuid,
        uom: row.uom || "",
        rate: row.rate ?? 1,
        price: row.price ?? 0,
        is_default: !!row.is_default,
      };
      this.unitErrors = {};
      this.unitModal = true;
    },
    onUnitUomInput(value) {
      this.unitForm.uom = (value || "").toUpperCase().replace(/\s+/g, "");
    },
    validateUnit() {
      const errors = {};
      if (!this.unitForm.uom) errors.uom = i18n.t("message.requiredX", [i18n.t("catalogSync.unit")]);
      const rate = Number(this.unitForm.rate);
      if (!(rate > 0)) errors.rate = i18n.t("message.invalidX", [i18n.t("catalogSync.rate")]);
      const price = Number(this.unitForm.price);
      if (Number.isNaN(price) || price < 0) errors.price = i18n.t("message.invalidX", [i18n.t("label.unitPrice")]);
      this.unitErrors = errors;
      return Object.keys(errors).length === 0;
    },
    async reloadUnits() {
      const itemCode = this.p.itemCode;
      await Promise.all([
        this.getCatalogUoms({ itemCode }),
        this.getProduct({ itemCode }),
      ]);
    },
    async saveUnit() {
      if (this.unitSaving || !this.validateUnit()) return;
      this.unitSaving = true;
      try {
        const data = {
          uom: this.unitForm.uom,
          rate: Number(this.unitForm.rate),
          price: Number(this.unitForm.price),
          is_default: !!this.unitForm.is_default,
        };
        const result = this.unitForm.uuid
          ? await this.updateCatalogUom({ uuid: this.unitForm.uuid, data })
          : await this.createCatalogUom({ itemCode: this.p.itemCode, data });
        if (result) {
          this.unitModal = false;
          await this.reloadUnits();
        }
      } finally {
        this.unitSaving = false;
      }
    },
    async removeUnit(row) {
      const ok = await this.$bvModal.msgBoxConfirm(
        this.$t("catalogSync.confirmDeleteUnit", [row.uom]),
        {
          okTitle: this.$t("label.delete"),
          cancelTitle: this.$t("label.cancel"),
          okVariant: "danger",
          centered: true,
        }
      );
      if (!ok) return;
      if (await this.deleteCatalogUom({ uuid: row.uuid, uom: row.uom })) await this.reloadUnits();
    },
    async toggleCanExchange(value) {
      if (this.flagSaving) return;
      const previous = this.canExchange;
      this.flagSaving = true;
      this.canExchange = value;
      try {
        await this.setCanExchange({ itemCode: this.p.itemCode, value });
      } catch (_) {
        this.canExchange = previous;
      } finally {
        this.flagSaving = false;
      }
    },
    async toggleShowInApp(value) {
      if (this.showInAppSaving) return;
      const previous = this.showInApp;
      this.showInAppSaving = true;
      this.showInApp = value;
      try {
        await this.setCatalogIsActive({ itemCode: this.p.itemCode, value });
      } catch (_) {
        this.showInApp = previous;
      } finally {
        this.showInAppSaving = false;
      }
    },
    async toggleIsShowingInSalesDocs(value) {
      if (this.showingFlagSaving) return;
      const previous = this.isShowingInSalesDocs;
      this.showingFlagSaving = true;
      this.isShowingInSalesDocs = value;
      try {
        await this.setIsShowingInSalesDocs({ itemCode: this.p.itemCode, value });
      } catch (_) {
        this.isShowingInSalesDocs = previous;
      } finally {
        this.showingFlagSaving = false;
      }
    },
    onFileChange(e) {
      const files = Array.from(e.target.files || []);
      this.newFiles = this.newFiles.concat(files);
      e.target.value = "";
    },
    removeExisting(idx) {
      this.mediaToRemove.add(idx);
      this.mediaToRemove = new Set(this.mediaToRemove);
    },
    removeNew(i) {
      this.newFiles.splice(i, 1);
    },
    async saveCatalog() {
      if (this.catalogSaving) return;
      this.catalogSaving = true;
      try {
        const fd = new FormData();
        fd.append("description", this.description || "");
        const keptPaths = (this.catalogDetails?.media || []).filter(
          (_, idx) => !this.mediaToRemove.has(idx)
        );
        fd.append("existing_media", JSON.stringify(keptPaths));
        this.newFiles.forEach((f) => fd.append("media[]", f));
        await this.upsertCatalogByItemCode({ itemCode: this.p.itemCode, data: fd });
        const d = this.catalogDetails || {};
        this.description = d.description || "";
        this.mediaUrls = d.mediaUrls || [];
        this.mediaToRemove = new Set();
        this.newFiles = [];
      } finally {
        this.catalogSaving = false;
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Product info (read-only from AutoCount) -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">
              <i class="mdi mdi-package-variant text-primary mr-1"></i>{{ p.description }}
            </h5>
            <dl class="row mb-0">
              <dt class="col-sm-3">{{ $t('label.itemCode') }}</dt>
              <dd class="col-sm-3">{{ p.itemCode || '-' }}</dd>
              <dt class="col-sm-3">{{ $t('label.uom') }}</dt>
              <dd class="col-sm-3">{{ p.baseUom || '-' }}</dd>

              <dt class="col-sm-3">{{ $t('module.product') }}</dt>
              <dd class="col-sm-3">{{ p.itemGroup || '-' }}</dd>
              <dt class="col-sm-3">{{ $t('label.status') }}</dt>
              <dd class="col-sm-3">
                <b-badge variant="success" v-if="p.isActive">{{ $t('label.active') }}</b-badge>
                <b-badge variant="secondary" v-else>{{ $t('label.inactive') }}</b-badge>
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>

    <!-- Units + prices (mirrored from the ledger, or One Sales' own) + Stock Balance -->
    <!-- The columns carry the gutter, not the cards: an h-100 card plus the
         theme's card margin overflows its column, and the next row paints
         over its bottom. -->
    <div class="row">
      <div class="col-12 col-lg-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body">
            <div class="d-flex align-items-start justify-content-between mb-1">
              <h6 class="card-title mb-0">
                <i class="mdi mdi-tag-outline text-primary mr-1"></i>{{ $t('catalogSync.units') }}
              </h6>
              <b-button v-if="canEditUnits" variant="primary" size="sm" @click="openAddUnit">
                <i class="mdi mdi-plus mr-1"></i>{{ $t('catalogSync.addUnit') }}
              </b-button>
            </div>
            <p class="text-muted small mb-2">
              <span v-if="isLite">{{ $t('catalogSync.localHint') }}</span>
              <span v-else>{{ $t('catalogSync.readOnlyHint', [driverLabel]) }}</span>
            </p>
            <b-table :items="unitRows" :fields="unitFields" small show-empty :empty-text="$t('catalogSync.noUnits')">
              <template #cell(rate)="data">
                {{ data.item.rate == null ? '-' : data.item.rate }}
              </template>
              <template #cell(is_default)="data">
                <b-badge v-if="data.item.is_default" variant="warning">
                  <i class="mdi mdi-star mr-1"></i>{{ $t('catalogSync.default') }}
                </b-badge>
                <span v-else class="text-muted">—</span>
              </template>
              <template #cell(action)="data">
                <template v-if="data.item.editable !== false">
                  <a href="javascript:void(0);" class="text-info mr-2" v-b-tooltip.hover :title="$t('catalogSync.updateUnit')" @click="openEditUnit(data.item)">
                    <i class="mdi mdi-pencil font-size-18"></i>
                  </a>
                  <a href="javascript:void(0);" class="text-danger" v-b-tooltip.hover :title="$t('label.delete')" @click="removeUnit(data.item)">
                    <i class="mdi mdi-delete font-size-18"></i>
                  </a>
                </template>
                <i v-else class="mdi mdi-lock-outline text-muted font-size-18" v-b-tooltip.hover :title="$t('catalogSync.lockedHint', [driverLabel])"></i>
              </template>
            </b-table>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body">
            <h6 class="card-title">
              <i class="mdi mdi-warehouse text-primary mr-1"></i>{{ $t('module.stockBalance') }}
            </h6>
            <p class="text-muted small mb-2">
              Current on-hand quantity per location (read-only, derived from Stock Operations).
            </p>
            <b-table :items="p.stockBalances || []" small :fields="[
              { key: 'location', label: $t('label.location') },
              { key: 'balance_qty', label: $t('label.qty') },
            ]" empty-text="No stock" show-empty></b-table>
          </div>
        </div>
      </div>
    </div>

    <!-- Flags -->
    <div class="row">
      <div class="col-12 col-lg-4">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title text-primary">
              <i class="mdi mdi-cellphone mr-1"></i>{{ $t('label.showInApp') }}
            </h6>
            <p class="text-muted small">
              When OFF, this product is hidden from all product lookups (sales doc item dropdowns, product listing, storefront) and cannot be added to new sales documents. Existing documents are unaffected.
            </p>
            <div v-if="flagLoading"><b-spinner small></b-spinner></div>
            <b-form-checkbox v-else switch :checked="showInApp" :disabled="showInAppSaving" @change="toggleShowInApp">
              {{ showInApp ? $t('label.active') : $t('label.inactive') }}
            </b-form-checkbox>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-4">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title text-primary">
              <i class="mdi mdi-swap-horizontal mr-1"></i>{{ $t('label.canExchange') }}
            </h6>
            <p class="text-muted small">
              When ON, every sold line of this product on a consignment transaction must split Sold qty into Starter + Exchange.
            </p>
            <div v-if="flagLoading"><b-spinner small></b-spinner></div>
            <b-form-checkbox v-else switch :checked="canExchange" :disabled="flagSaving" @change="toggleCanExchange">
              {{ canExchange ? $t('label.active') : $t('label.inactive') }}
            </b-form-checkbox>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-4">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title text-primary">
              <i class="mdi mdi-eye-outline mr-1"></i>{{ $t('label.showInSalesDocs') }}
            </h6>
            <p class="text-muted small">
              When ON, this product is auto-populated into every new consignment transaction so the sales agent always sees it.
            </p>
            <div v-if="flagLoading"><b-spinner small></b-spinner></div>
            <b-form-checkbox v-else switch :checked="isShowingInSalesDocs" :disabled="showingFlagSaving" @change="toggleIsShowingInSalesDocs">
              {{ isShowingInSalesDocs ? $t('label.active') : $t('label.inactive') }}
            </b-form-checkbox>
          </div>
        </div>
      </div>
    </div>

    <!-- Catalog (merged): description + media -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title">
              <i class="mdi mdi-book-open-page-variant text-primary mr-1"></i>{{ $t('module.catalog') }}
            </h6>
            <p class="text-muted small">
              Rich description + media for this product. Shown to sales agents and customers.
            </p>

            <b-form-group :label="$t('label.description')">
              <ckeditor v-if="!flagLoading" :editor="editor" v-model="description" :config="editorConfig" />
              <b-spinner v-else small></b-spinner>
            </b-form-group>

            <b-form-group label="Media">
              <div v-if="visibleMedia.length" class="media-grid mb-2">
                <div v-for="m in visibleMedia" :key="'e'+m.idx" class="media-tile">
                  <img :src="m.url" />
                  <button type="button" class="remove-btn" @click="removeExisting(m.idx)">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </div>
              <div v-if="newFiles.length" class="media-grid mb-2">
                <div v-for="(f, i) in newFiles" :key="'n'+i" class="media-tile media-tile-new">
                  <div class="new-label"><i class="mdi mdi-plus"></i> {{ f.name }}</div>
                  <button type="button" class="remove-btn" @click="removeNew(i)">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </div>
              <input type="file" accept="image/*" multiple @change="onFileChange" />
            </b-form-group>

            <div class="text-right">
              <b-button variant="primary" :disabled="catalogSaving" @click="saveCatalog">
                <b-spinner v-if="catalogSaving" small class="mr-1"></b-spinner>
                {{ $t('label.submit') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Add / edit a unit (lite tenants only) -->
    <b-modal
      v-model="unitModal"
      :title="unitForm.uuid ? $t('catalogSync.updateUnit') : $t('catalogSync.addUnit')"
      hide-footer
      centered
    >
      <b-form @submit.prevent="saveUnit">
        <b-row>
          <b-col cols="12">
            <b-form-group :label="$t('catalogSync.unit')">
              <b-form-input :value="unitForm.uom" maxlength="20" :state="unitErrors.uom ? false : null" @input="onUnitUomInput" />
              <small v-if="unitErrors.uom" class="text-danger">{{ unitErrors.uom }}</small>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="$t('catalogSync.rate')">
              <b-form-input type="number" min="0" step="any" v-model="unitForm.rate" :state="unitErrors.rate ? false : null" />
              <small class="text-muted">{{ $t('catalogSync.rateHint') }}</small>
              <small v-if="unitErrors.rate" class="text-danger d-block">{{ unitErrors.rate }}</small>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="$t('label.unitPrice')">
              <b-form-input type="number" min="0" step="0.01" v-model="unitForm.price" :state="unitErrors.price ? false : null" />
              <small v-if="unitErrors.price" class="text-danger">{{ unitErrors.price }}</small>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group>
              <b-form-checkbox v-model="unitForm.is_default" switch>{{ $t('catalogSync.default') }}</b-form-checkbox>
            </b-form-group>
          </b-col>
          <b-col cols="12" class="text-right">
            <b-button variant="light" class="mr-2" @click="unitModal = false">{{ $t('label.cancel') }}</b-button>
            <b-button type="submit" variant="primary" :disabled="unitSaving">
              <b-spinner v-if="unitSaving" small class="mr-1"></b-spinner>{{ $t('label.submit') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
  </Layout>
</template>

<style scoped>
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}
.media-tile {
  position: relative;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  background: #fafbfc;
}
.media-tile img { width: 100%; height: 100%; object-fit: cover; }
.media-tile-new {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #7C4DFF;
  background: rgba(124, 77, 255, 0.05);
}
.new-label {
  font-size: 11px;
  color: #7C4DFF;
  text-align: center;
  padding: 6px;
  word-break: break-all;
}
.remove-btn {
  position: absolute;
  top: 4px; right: 4px;
  width: 22px; height: 22px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}
.remove-btn:hover { background: rgba(220, 53, 69, 0.9); }
</style>
